import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { FaClock, FaDollarSign, FaTasks, FaCheckCircle, FaSpinner, FaCalendarAlt, FaUserTie } from 'react-icons/fa';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';

interface ProjectData {
  id: string;
  businessId: string;
  name: string;
  description: string;
  status: string;
  startDate: any;
  endDate: any;
  totalAmount: number;
  progress: number;
  tasks: any[];
  clientName?: string;
  businessName?: string;
  myTasksCount?: number;
  myPayment?: number;
}



const WorkerProjects: React.FC = () => {
  const { currentUser, userData } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState<ProjectData | null>(null);
  const [showProjectModal, setShowProjectModal] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (!userData) return;
    fetchWorkerProjects();
  }, [currentUser, userData, navigate]);

  const fetchWorkerProjects = async () => {
    if (!currentUser || !userData) return;
    try {
      setLoading(true);
      const db = getFirestore();
      let allProjects: ProjectData[] = [];
      let walletTransactions: any[] = [];
      // Obtener transacciones del wallet del usuario
      const walletRef = collection(db, `wallet/${currentUser.uid}/transacciones`);
      const walletSnap = await getDocs(walletRef);
      walletTransactions = walletSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Solo cargar proyectos de userData.proyectosAsignados
      if (Array.isArray(userData.proyectosAsignados)) {
        for (const projectId of userData.proyectosAsignados) {
          // Buscar el businessId correspondiente (de tareasAsignadas o businessesAsignados)
          let businessId = null;
          if (Array.isArray(userData.tareasAsignadas)) {
            const tarea = userData.tareasAsignadas.find((t: any) => t.projectId === projectId);
            if (tarea) businessId = tarea.businessId;
          }
          if (!businessId && Array.isArray(userData.businessesAsignados) && userData.businessesAsignados.length > 0) {
            businessId = userData.businessesAsignados[0]; // fallback
          }
          if (businessId) {
            try {
              const projectRef = collection(db, `projects/${businessId}/projects`);
              const projectSnap = await getDocs(projectRef);
              const projectDoc = projectSnap.docs.find(doc => doc.id === projectId);
              if (projectDoc) {
                const projectData = projectDoc.data() as any;
                // Filtrar transacciones del wallet para este proyecto
                const projectTransactions = walletTransactions.filter(trx => trx.projectId === projectId);
                // El número de tareas es igual al número de transacciones para este proyecto
                const myTasksCount = projectTransactions.length;
                // El pago es la suma del campo 'total' de las transacciones
                const myPayment = projectTransactions.reduce((sum, trx) => sum + (trx.total || 0), 0);
                let myTasks: any[] = [];
                
                // Siempre leer desde la subcolección (nueva estructura)
                const tasksRef = collection(db, `projects/${businessId}/projects/${projectId}/tasks`);
                const tasksSnap = await getDocs(tasksRef);
                myTasks = tasksSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                  .filter((task: any) => {
                    // Verificar assignedTo
                    if (task.assignedTo === currentUser.uid) {
                      return true;
                    }
                    // Verificar worker (puede ser string o array)
                    if (task.worker) {
                      if (Array.isArray(task.worker)) {
                        return task.worker.includes(currentUser.uid);
                      } else if (typeof task.worker === 'string') {
                        return task.worker === currentUser.uid;
                      }
                    }
                    return false;
                  });
                
                console.log(`[WorkerProjects] Tareas encontradas para proyecto ${projectId}:`, myTasks.length, myTasks);
                // Calcular progreso como promedio de progress de las tareas asignadas
                const myTasksProgress = myTasks.length > 0
                  ? Math.round(myTasks.reduce((total: number, task: any) => total + (typeof task.progress === 'number' ? task.progress : 0), 0) / myTasks.length)
                  : 0;
                // Obtener nombre del negocio
                const businessSnap = await getDocs(collection(db, 'businesses'));
                const businessDoc = businessSnap.docs.find(doc => doc.id === businessId);
                const businessData = businessDoc ? businessDoc.data() : {};
                const project: ProjectData = {
                  id: projectDoc.id,
                  businessId,
                  name: projectData.name,
                  description: projectData.description || '',
                  status: projectData.status || 'active',
                  startDate: projectData.startDate,
                  endDate: projectData.endDate,
                  totalAmount: projectData.totalAmount || 0,
                  progress: myTasksProgress,
                  tasks: myTasks,
                  clientName: projectData.clientName,
                  businessName: businessData.name || businessData.businessName,
                  myTasksCount,
                  myPayment
                };
                allProjects.push(project);
              }
            } catch (e) { /* ignorar errores individuales */ }
          }
        }
      }
      setProjects(allProjects);
      console.log('Proyectos del worker encontrados:', allProjects.length);
    } catch (error) {
      console.error('Error al obtener proyectos del worker:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return t('workerProjects.status.active');
      case 'completed': return t('workerProjects.status.completed');
      case 'pending': return t('workerProjects.status.pending');
      default: return status;
    }
  };

  const getTaskStatusColor = (columnId: string) => {
    switch (columnId) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'done': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTaskStatusText = (columnId: string) => {
    switch (columnId) {
      case 'new': return t('workerProjects.taskStatus.new');
      case 'in_progress': return t('workerProjects.taskStatus.inProgress');
      case 'done': return t('workerProjects.taskStatus.done');
      default: return columnId;
    }
  };

  const formatDate = (date: any) => {
    if (!date) return '-';
    if (date.seconds) {
      return new Date(date.seconds * 1000).toLocaleDateString(i18n.language);
    }
    return new Date(date).toLocaleDateString(i18n.language);
  };

  const formatDateTime = (date: any, time?: string) => {
    if (!date) return '-';
    const dateStr = formatDate(date);
    return time ? `${dateStr} ${time}` : dateStr;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-8">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-red-600 mx-auto mb-4" />
            <p className="text-gray-600">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-3xl shadow-2xl p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {t('workerProjects.title', 'Mis Proyectos')}
              </h1>
              <p className="text-gray-600">
                {t('workerProjects.description', 'Proyectos en los que estás participando como trabajador')}
              </p>
            </div>
            <button
              onClick={() => navigate('/worker-dashboard')}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors duration-200"
            >
              {t('workerProjects.backToDashboard')}
            </button>
          </div>

          {/* Estadísticas generales */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-100 text-sm">{t('workerProjects.totalProjects')}</p>
                  <p className="text-3xl font-bold">{projects.length}</p>
                </div>
                <HiOutlineDocumentText className="text-4xl opacity-80" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">{t('workerProjects.totalTasks')}</p>
                  <p className="text-3xl font-bold">{projects.reduce((total, project) => total + (project.myTasksCount ?? 0), 0)}</p>
                </div>
                <FaTasks className="text-4xl opacity-80" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">{t('workerProjects.totalEarnings')}</p>
                  <p className="text-3xl font-bold">
                    ${projects.reduce((total, project) => total + (project.myPayment ?? 0), 0).toLocaleString()}
                  </p>
                </div>
                <FaDollarSign className="text-4xl opacity-80" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">{t('workerProjects.averageProgress')}</p>
                  <p className="text-3xl font-bold">
                    {projects.length > 0
                      ? Math.round(projects.reduce((total, project) => total + (project.progress || 0), 0) / projects.length)
                      : 0}%
                  </p>
                </div>
                <FaCheckCircle className="text-4xl opacity-80" />
              </div>
            </div>
          </div>
        </div>

        {/* Lista de proyectos */}
        {projects.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-2xl p-12 text-center">
            <HiOutlineDocumentText className="text-6xl text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-700 mb-2">No tienes proyectos asignados</h2>
            <p className="text-gray-500">Cuando te asignen tareas en proyectos, aparecerán aquí.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {projects.map((project) => (
              <div
                key={project.id}
                className="bg-white rounded-3xl shadow-2xl p-8 hover:shadow-3xl transition-all duration-300 hover:-translate-y-2 cursor-pointer hover:shadow-xl transition-all"
                onClick={() => navigate(`/worker-projects/${project.id}`)}
                tabIndex={0}
                role="button"
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate(`/worker-projects/${project.id}`); }}
              >
                {/* Header del proyecto */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{project.name}</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                        {getStatusText(project.status)}
                      </span>
                      {project.businessName && (
                        <span className="flex items-center gap-1">
                          <FaUserTie className="text-red-500" />
                          {project.businessName}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Descripción */}
                {project.description && (
                  <p className="text-gray-600 mb-6">{project.description}</p>
                )}

                {/* Información del proyecto */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <FaCalendarAlt className="text-red-500" />
                    <span>{t('workerProjects.startDate')}: {formatDate(project.startDate)}</span>
                  </div>
                  {project.endDate && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FaCalendarAlt className="text-red-500" />
                      <span>{t('workerProjects.endDate')}: {formatDate(project.endDate)}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <FaTasks className="text-red-500" />
                    <span>{t('workerProjects.myTasks')}: {project.myTasksCount ?? 0}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <FaDollarSign className="text-red-500" />
                    <span>{t('workerProjects.myPayment')}: ${project.myPayment?.toLocaleString() ?? 0}</span>
                  </div>
                </div>

                {/* Progreso */}
                <div className="mb-6">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-600">{t('workerProjects.myProgress')}</span>
                    <span className="font-semibold text-red-600">{project.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-red-500 to-pink-500 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${project.progress}%` }}
                    ></div>
                  </div>
                </div>

                {/* Lista de tareas */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900 mb-3">{t('workerProjects.myTasksInProject')}</h4>
                  {project.tasks.map((task) => (
                    <div key={task.id} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                      <div className="flex items-start justify-between mb-2">
                        <h5 className="font-semibold text-gray-800 text-sm">{task.title}</h5>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTaskStatusColor(task.columnId)}`}>
                          {getTaskStatusText(task.columnId)}
                        </span>
                      </div>
                      
                      {task.description && (
                        <p className="text-xs text-gray-600 mb-2">{task.description}</p>
                      )}

                      <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                        {task.startDate && (
                          <span>📅 {formatDateTime(task.startDate, task.startTime)}</span>
                        )}
                        {task.duration && (
                          <span>⏱️ {task.duration} {task.durationUnit === 'days' ? 'días' : 'horas'}</span>
                        )}
                        {task.finalPrice && (
                          <span>💰 ${task.finalPrice} {task.finalPriceUnit === 'hour' ? '/hora' : '/día'}</span>
                        )}
                        {task.finalTotal && (
                          <span>💵 Total: ${task.finalTotal}</span>
                        )}
                      </div>

                      {/* Progreso de la tarea */}
                      {task.checklistEnabled && task.checklist && task.checklist.length > 0 && (
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                            <span>Checklist</span>
                            <span>{task.progress || 0}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div 
                              className="bg-red-500 h-1.5 rounded-full transition-all duration-300"
                              style={{ width: `${task.progress || 0}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Botón para ver más detalles */}
                <button
                  onClick={() => {
                    setSelectedProject(project);
                    setShowProjectModal(true);
                  }}
                  className="w-full mt-6 bg-red-600 hover:bg-red-700 text-white py-3 rounded-xl font-semibold transition-colors duration-200"
                >
                  {t('workerProjects.seeFullDetails')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de detalles del proyecto */}
      {showProjectModal && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-gray-900">{selectedProject.name}</h2>
                <button
                  onClick={() => setShowProjectModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Información detallada del proyecto */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Información del Proyecto</h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-gray-500">Estado:</span>
                      <span className={`ml-2 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedProject.status)}`}>
                        {getStatusText(selectedProject.status)}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Cliente:</span>
                      <span className="ml-2 text-gray-900">{selectedProject.clientName || 'No especificado'}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Negocio:</span>
                      <span className="ml-2 text-gray-900">{selectedProject.businessName || 'No especificado'}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Fecha de inicio:</span>
                      <span className="ml-2 text-gray-900">{formatDate(selectedProject.startDate)}</span>
                    </div>
                    {selectedProject.endDate && (
                      <div>
                        <span className="text-sm text-gray-500">Fecha de fin:</span>
                        <span className="ml-2 text-gray-900">{formatDate(selectedProject.endDate)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Mis Estadísticas</h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-gray-500">Tareas asignadas:</span>
                      <span className="ml-2 text-gray-900 font-semibold">{selectedProject.tasks.length}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Progreso promedio:</span>
                      <span className="ml-2 text-gray-900 font-semibold">{selectedProject.progress}%</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Ganancias totales:</span>
                      <span className="ml-2 text-gray-900 font-semibold">
                        ${selectedProject.tasks.reduce((sum, task) => sum + (task.finalTotal || 0), 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Descripción */}
              {selectedProject.description && (
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Descripción</h3>
                  <p className="text-gray-600">{selectedProject.description}</p>
                </div>
              )}

              {/* Lista detallada de tareas */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Mis Tareas Detalladas</h3>
                <div className="space-y-4">
                  {selectedProject.tasks.map((task) => (
                    <div key={task.id} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <div className="flex items-start justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900">{task.title}</h4>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getTaskStatusColor(task.columnId)}`}>
                          {getTaskStatusText(task.columnId)}
                        </span>
                      </div>

                      {task.description && (
                        <p className="text-gray-600 mb-4">{task.description}</p>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <FaCalendarAlt className="text-red-500" />
                            <span className="text-gray-500">Fecha y hora:</span>
                            <span className="text-gray-900">{formatDateTime(task.startDate, task.startTime)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <FaClock className="text-red-500" />
                            <span className="text-gray-500">Duración:</span>
                            <span className="text-gray-900">{task.duration} {task.durationUnit === 'days' ? 'días' : 'horas'}</span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <FaDollarSign className="text-red-500" />
                            <span className="text-gray-500">Precio:</span>
                            <span className="text-gray-900">${task.finalPrice} {task.finalPriceUnit === 'hour' ? '/hora' : '/día'}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <FaDollarSign className="text-red-500" />
                            <span className="text-gray-500">Total a recibir:</span>
                            <span className="text-gray-900 font-semibold">${task.finalTotal}</span>
                          </div>
                        </div>
                      </div>

                      {/* Progreso del checklist */}
                      {task.checklistEnabled && task.checklist && task.checklist.length > 0 && (
                        <div className="mb-4">
                          <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-gray-600 font-medium">Progreso del Checklist</span>
                            <span className="font-semibold text-red-600">{task.progress || 0}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-red-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${task.progress || 0}%` }}
                            ></div>
                          </div>
                          <div className="mt-3 space-y-2">
                            {task.checklist.map((item: any, index: number) => (
                              <div key={item.id || index} className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded-full ${item.completed ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                <span className={`text-sm ${item.completed ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                                  {item.text}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerProjects; 